import { darken, tint } from "polished";
import { useSelector } from "react-redux";
import { State } from "../../app/store";

type Colors = {
  color50: string;
  color100: string;
  color200: string;
  color300: string;
  color400: string;
  color500: string;
  color600: string;
  color700: string;
  color800: string;
  color900: string;
  color950: string;
};

// Funkcja sprawdzająca poprawność formatu koloru
const isValidColor = (color: string): boolean => {
  // Regex do sprawdzania HEX, RGB, RGBA, HSL i HSLA
  const hexRegex = /^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/;
  const rgbRegex = /^rgb\((\d{1,3}), (\d{1,3}), (\d{1,3})\)$/;
  const rgbaRegex = /^rgba\((\d{1,3}), (\d{1,3}), (\d{1,3}), (0|0?\.\d+|1)\)$/;
  const hslRegex = /^hsl\((\d{1,3}), (\d{1,3})%, (\d{1,3})%\)$/;
  const hslaRegex = /^hsla\((\d{1,3}), (\d{1,3})%, (\d{1,3})%, (0|0?\.\d+|1)\)$/;

  return (
    hexRegex.test(color) ||
    rgbRegex.test(color) ||
    rgbaRegex.test(color) ||
    hslRegex.test(color) ||
    hslaRegex.test(color)
  );
};

export default function useColorPalette() {
  const saleConfiguration = useSelector((state: State) => state.config.configuration);
  const defaultPrimaryColor = "#265BAA"; // Domyślny kolor primary
  const defaultSecondaryColor = "#F68B1E"; // Domyślny kolor secondary
  const defaultBackgroundColor = "#FAFAFA"; // Domyślny kolor background

  // Sprawdzenie, czy saleConfiguration.actionColor jest obiektem zawierającym HEX
  const basePrimaryColor =
    saleConfiguration &&
    typeof saleConfiguration.actionColor === "string" &&
    isValidColor(saleConfiguration.actionColor)
      ? saleConfiguration.actionColor
      : defaultPrimaryColor;
  const baseSecondaryColor =
    saleConfiguration &&
    typeof saleConfiguration.ticketTextColor === "string" &&
    isValidColor(saleConfiguration.ticketTextColor)
      ? saleConfiguration.ticketTextColor
      : defaultSecondaryColor;
  const baseBackgroundColor =
    saleConfiguration &&
    typeof saleConfiguration.backgroundColor === "string" &&
    isValidColor(saleConfiguration.backgroundColor)
      ? saleConfiguration.backgroundColor
      : defaultBackgroundColor;

  const generateColors = (color: string) => ({
    color50: tint(0.9, color),
    color100: tint(0.6, color),
    color200: tint(0.3, color),
    color300: tint(0.2, color),
    color400: tint(0.1, color),
    color500: color,
    color600: darken(0.1, color),
    color700: darken(0.2, color),
    color800: darken(0.3, color),
    color900: darken(0.4, color),
    color950: darken(0.5, color),
  });

  const primaryColors: Colors = generateColors(basePrimaryColor);
  const secondaryColors: Colors = generateColors(baseSecondaryColor);

  (Object.keys(primaryColors) as Array<keyof Colors>).forEach((key) => {
    document.documentElement.style.setProperty(`--primary-${key}`, primaryColors[key]);
  });

  (Object.keys(secondaryColors) as Array<keyof Colors>).forEach((key) => {
    document.documentElement.style.setProperty(`--secondary-${key}`, secondaryColors[key]);
  });

  document.documentElement.style.setProperty("--background-color-global", baseBackgroundColor);
}
